// src/components/NetworkPanel.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { readToken } from './AccountPanel';

/**
 * The stakeholder directory, and the connections between people in it.
 *
 * THREE TABS, ONE IDEA. Browse finds people, Requests is what needs an answer
 * from you, People is who you already work with. Requests carries a count
 * because it is the only one where someone is waiting on you.
 *
 * WHAT A CARD SHOWS, AND WHY IT STOPS THERE
 * Browsing shows that a credential exists, what kind, who issued it and how far
 * it has been checked — never the number. The number appears when someone asks
 * to connect with you, because that is the moment you are being asked to judge
 * it, and they chose to approach you. Phone and email wait for the accept.
 *
 * The panel does not decide any of that; the server does, from the stored
 * relationship. This just renders whichever fields came back.
 */

const T = {
  surface: 'var(--surface-2,#1a2234)',
  border: 'var(--border,#3a4456)',
  text: 'var(--text,#e6ebf5)',
  dim: 'var(--text-dim,#9aa7c0)',
  accent: 'var(--accent)',
  ok: 'var(--ok)',
  warn: 'var(--warn)',
};

const CRED_LABEL = {
  coa: 'CoA', rera: 'RERA', gstin: 'GSTIN', pwd: 'PWD', municipal: 'Municipal',
  iiid: 'IIID', iei: 'IE(I)', cin: 'CIN', udyam: 'Udyam', degree: 'Qualification',
};

export default function NetworkPanel({ apiBase }) {
  const [tab, setTab] = useState('browse');
  const [filters, setFilters] = useState(null);
  const [q, setQ] = useState('');
  const [role, setRole] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [people, setPeople] = useState([]);
  const [requests, setRequests] = useState({ incoming: [], outgoing: [] });
  const [connections, setConnections] = useState([]);
  const [notice, setNotice] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [signedIn, setSignedIn] = useState(() => !!readToken());

  const call = useCallback(async (path, opts = {}) => {
    const tok = readToken();
    const res = await fetch(`${apiBase}/api/network/${path}`, {
      ...opts,
      headers: {
        ...(opts.body ? { 'Content-Type': 'application/json' } : {}),
        ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
      },
    });
    const d = await res.json().catch(() => ({}));
    return { ok: res.ok, status: res.status, d };
  }, [apiBase]);

  // The directory is signed-in only, so there is nothing to show and no point
  // asking. Say why instead of rendering an empty list that looks broken.
  useEffect(() => {
    const onAuth = (e) => setSignedIn(!!e.detail?.signedIn);
    window.addEventListener('decon:auth', onAuth);
    return () => window.removeEventListener('decon:auth', onAuth);
  }, []);

  useEffect(() => {
    if (!signedIn) return;
    call('filters').then(({ ok, d }) => { if (ok) setFilters(d); });
  }, [call, signedIn]);

  const loadDirectory = useCallback(async () => {
    const p = new URLSearchParams();
    if (q.trim()) p.set('q', q.trim());
    if (role) p.set('role', role);
    if (stateFilter) p.set('state', stateFilter);
    const { ok, d, status } = await call(`directory?${p}`);
    if (status === 401) { setSignedIn(false); return; }
    if (ok) setPeople(d.people || []);
  }, [call, q, role, stateFilter]);

  const loadRequests = useCallback(async () => {
    const { ok, d } = await call('requests');
    if (ok) setRequests({ incoming: d.incoming || [], outgoing: d.outgoing || [] });
  }, [call]);

  const loadConnections = useCallback(async () => {
    const { ok, d } = await call('connections');
    if (ok) setConnections(d.people || []);
  }, [call]);

  useEffect(() => { if (signedIn) { loadRequests(); loadConnections(); } }, [signedIn, loadRequests, loadConnections]);

  // debounce the directory so typing does not fire a query per keystroke
  useEffect(() => {
    if (!signedIn || tab !== 'browse') return undefined;
    const t = setTimeout(loadDirectory, 250);
    return () => clearTimeout(t);
  }, [signedIn, tab, loadDirectory]);

  const refreshAll = () => { loadDirectory(); loadRequests(); loadConnections(); };

  const connect = async (person) => {
    setBusyId(person.id); setNotice(null);
    const { ok, d } = await call('connect', {
      method: 'POST', body: JSON.stringify({ userId: person.id }),
    });
    setBusyId(null);
    if (!ok) { setNotice(d.error || 'Could not send that request.'); return; }
    setNotice(d.state === 'connected'
      ? `You and ${person.displayName} are now connected — they had already asked.`
      : `Request sent to ${person.displayName}.`);
    refreshAll();
  };

  const respond = async (connectionId, accept) => {
    setBusyId(connectionId); setNotice(null);
    const { ok, d } = await call('respond', {
      method: 'POST', body: JSON.stringify({ connectionId, accept }),
    });
    setBusyId(null);
    if (!ok) { setNotice(d.error || 'Could not answer that request.'); return; }
    refreshAll();
  };

  const disconnect = async (person) => {
    setBusyId(person.id); setNotice(null);
    const { ok, d } = await call(`connection/${person.id}`, { method: 'DELETE' });
    setBusyId(null);
    if (!ok) { setNotice(d.error || 'Could not do that.'); return; }
    refreshAll();
  };

  if (!signedIn) {
    return (
      <div style={S.wrap}>
        <div style={S.empty}>
          <div style={S.emptyHead}>Sign in to see the network</div>
          <div style={S.hint}>
            The directory lists real professionals with their firm, city and
            registration numbers. It is not open to browse without an account.
          </div>
        </div>
      </div>
    );
  }

  const roleLabel = (key) => filters?.roles?.find((r) => r.key === key)?.label || key;

  return (
    <div style={S.wrap}>
      <div style={S.tabs}>
        <Tab on={tab === 'browse'} onClick={() => setTab('browse')}>Browse</Tab>
        <Tab on={tab === 'requests'} onClick={() => setTab('requests')}
             badge={requests.incoming.length}>Requests</Tab>
        <Tab on={tab === 'people'} onClick={() => setTab('people')}
             badge={connections.length} muted>People</Tab>
      </div>

      {notice && <div style={S.notice} onClick={() => setNotice(null)}>{notice}</div>}

      {tab === 'browse' && (
        <>
          <input style={S.input} value={q} placeholder="Search by name or firm…"
                 onChange={(e) => setQ(e.target.value)} />
          <div style={S.row}>
            <select style={S.input} value={role} onChange={(e) => setRole(e.target.value)}>
              <option value="">Every role</option>
              {(filters?.roles || []).map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
            <select style={S.input} value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}>
              <option value="">Anywhere</option>
              {(filters?.states || []).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {!people.length && (
            <div style={S.hint}>
              Nobody matches that yet. The directory only holds people who have
              registered — it will fill as testers join.
            </div>
          )}
          {people.map((p) => (
            <PersonCard key={p.id} person={p} roleLabel={roleLabel} busy={busyId === p.id}
                        onConnect={() => connect(p)} onDisconnect={() => disconnect(p)} />
          ))}
        </>
      )}

      {tab === 'requests' && (
        <>
          <div style={S.sectionHead}>Waiting for you</div>
          {!requests.incoming.length && <div style={S.hint}>Nobody is waiting on you.</div>}
          {requests.incoming.map((r) => (
            <PersonCard key={r.id} person={r.person} roleLabel={roleLabel} note={r.note}
                        busy={busyId === r.id}
                        onAccept={() => respond(r.id, true)}
                        onDecline={() => respond(r.id, false)} />
          ))}

          <div style={S.sectionHead}>You asked</div>
          {!requests.outgoing.length && <div style={S.hint}>You have not asked anyone yet.</div>}
          {requests.outgoing.map((r) => (
            <PersonCard key={r.id} person={r.person} roleLabel={roleLabel} pending
                        busy={busyId === r.person.id}
                        onDisconnect={() => disconnect(r.person)} />
          ))}
        </>
      )}

      {tab === 'people' && (
        <>
          {!connections.length && (
            <div style={S.hint}>
              No connections yet. Find people under Browse — once someone accepts,
              their phone, email and registration numbers become visible here.
            </div>
          )}
          {connections.map((p) => (
            <PersonCard key={p.id} person={p} roleLabel={roleLabel} busy={busyId === p.id}
                        onDisconnect={() => disconnect(p)} />
          ))}
        </>
      )}
    </div>
  );
}

/**
 * Hand off to the Message panel. A window event rather than a prop chain:
 * Network sits in the right pane and Message replaces it, so the two never
 * render together and cannot pass anything directly. App listens, switches the
 * pane and forwards the id.
 */
const messagePerson = (userId) => {
  window.dispatchEvent(new CustomEvent('decon:message-with', { detail: { userId } }));
};

function Tab({ on, onClick, children, badge, muted }) {
  return (
    <button style={{ ...S.tab, ...(on ? S.tabOn : null) }} onClick={onClick}>
      {children}
      {badge > 0 && (
        <span style={{ ...S.badge, ...(muted ? S.badgeMuted : null) }}>{badge}</span>
      )}
    </button>
  );
}

/**
 * One person. Which buttons appear is driven by relationship.state from the
 * server, so the panel cannot offer an action the server would refuse.
 */
function PersonCard({ person, roleLabel, note, pending, busy, onConnect, onDisconnect, onAccept, onDecline }) {
  const rel = person.relationship?.state || (pending ? 'sent' : 'none');
  // Full values arrive only when the server decided this viewer may have them;
  // otherwise credentialSummary carries kind/issuer/tier and nothing else.
  const creds = person.credentials?.length ? person.credentials : (person.credentialSummary || []);
  const revealed = !!person.credentials?.some((c) => c.value && !c.value.includes('•'));

  return (
    <div style={S.card}>
      <div style={S.name}>
        {person.displayName}
        {person.verified?.email && person.verified?.phone && (
          <span style={S.verifiedDot} title="Email and mobile verified">✓</span>
        )}
      </div>
      <div style={S.sub}>
        {roleLabel(person.role)}
        {person.firmName ? ` · ${person.firmName}` : ''}
        {person.city ? ` · ${person.city}` : ''}{person.state ? `, ${person.state}` : ''}
      </div>

      {person.experience?.years != null && (
        <div style={S.sub}>
          {person.experience.years} yrs
          {person.experience.projectsBand ? ` · ${person.experience.projectsBand} projects` : ''}
          {person.experience.projectTypes?.length ? ` · ${person.experience.projectTypes.join(', ')}` : ''}
        </div>
      )}

      {!!creds.length && (
        <div style={S.credList}>
          {creds.map((c, i) => (
            <div key={i} style={S.credRow}>
              <span style={S.credKind}>{CRED_LABEL[c.kind] || c.kind}</span>
              {c.value && <span style={S.credVal}>{c.value}</span>}
              {c.issuer && <span style={S.sub}>{c.issuer}</span>}
              <span style={{ ...S.tier, ...tierStyle(c.tier) }}>{c.tier}</span>
            </div>
          ))}
        </div>
      )}

      {(person.email || person.phone) && (
        <div style={S.contact}>{[person.phone, person.email].filter(Boolean).join('  ·  ')}</div>
      )}

      {note && <div style={S.note}>“{note}”</div>}

      {onAccept && (
        <div style={S.hint}>
          {revealed
            ? 'They have shown you their registration numbers so you can check them before accepting.'
            : 'This person has not listed a registration number.'}
        </div>
      )}

      <div style={S.actions}>
        {onAccept && <button style={S.primary} disabled={busy} onClick={onAccept}>Accept</button>}
        {onDecline && <button style={S.ghost} disabled={busy} onClick={onDecline}>Decline</button>}
        {rel === 'none' && onConnect && (
          <button style={S.primary} disabled={busy} onClick={onConnect}>Connect</button>
        )}
        {/* Only when the server says a message would be accepted — see
            canMessage in networkDb. A button that opens a thread you cannot
            write in is worse than no button. */}
        {person.canMessage && (
          <button style={S.ghost} onClick={() => messagePerson(person.id)}>Message</button>
        )}
        {rel === 'sent' && onDisconnect && (
          <button style={S.ghost} disabled={busy} onClick={onDisconnect}>Withdraw</button>
        )}
        {rel === 'connected' && onDisconnect && (
          <button style={S.ghost} disabled={busy} onClick={onDisconnect}>Disconnect</button>
        )}
        {rel === 'received' && <span style={S.state}>Waiting for your answer</span>}
        {rel === 'refused' && (
          <span style={S.state}>
            Not accepted. You can ask again after {String(person.relationship.retryAfter).slice(0, 10)}.
          </span>
        )}
        {rel === 'declined' && <span style={S.state}>You declined this request.</span>}
      </div>
    </div>
  );
}

function tierStyle(tier) {
  if (tier === 'verified') return { background: 'color-mix(in srgb, var(--ok) 18%, transparent)', color: 'var(--ok)' };
  if (tier === 'checked') return { background: 'color-mix(in srgb, var(--warn) 18%, transparent)', color: T.warn };
  return { background: 'var(--surface-2)', color: T.dim };
}

const S = {
  wrap: { display: 'flex', flexDirection: 'column', gap: 10, padding: 14, maxWidth: 460 },
  tabs: { display: 'flex', gap: 6 },
  tab: { flex: 1, fontSize: 12, fontWeight: 600, padding: '7px 10px', borderRadius: 8,
    cursor: 'pointer', background: 'var(--btn-face,#313d4b)', color: 'var(--btn-ink,#e8eef7)',
    border: `1px solid ${T.border}`, display: 'flex', alignItems: 'center',
    justifyContent: 'center', gap: 6 },
  tabOn: { background: T.surface, color: T.text, border: `1px solid ${T.accent}` },
  badge: { fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 9,
    background: T.accent, color: '#fff' },
  badgeMuted: { background: 'var(--surface-3)', color: T.dim },
  input: { width: '100%', fontSize: 12.5, padding: '7px 9px', borderRadius: 7,
    background: T.surface, color: T.text, border: `1px solid ${T.border}` },
  row: { display: 'flex', gap: 8 },
  card: { border: `1px solid ${T.border}`, borderRadius: 9, padding: 11,
    display: 'flex', flexDirection: 'column', gap: 5, background: T.surface },
  name: { fontSize: 14, fontWeight: 600, color: T.text, display: 'flex',
    alignItems: 'center', gap: 6 },
  verifiedDot: { fontSize: 11, color: T.ok },
  sub: { fontSize: 11, color: T.dim },
  credList: { display: 'flex', flexDirection: 'column', gap: 4, marginTop: 2 },
  credRow: { display: 'flex', alignItems: 'center', gap: 7, fontSize: 11, flexWrap: 'wrap' },
  credKind: { color: T.dim, minWidth: 62 },
  credVal: { color: T.text, fontFamily: 'ui-monospace, Menlo, monospace' },
  tier: { fontSize: 9, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
    padding: '1px 5px', borderRadius: 4 },
  contact: { fontSize: 11.5, color: T.text, fontFamily: 'ui-monospace, Menlo, monospace',
    marginTop: 2 },
  note: { fontSize: 11.5, color: T.text, fontStyle: 'italic', borderLeft: `2px solid ${T.border}`,
    paddingLeft: 8, marginTop: 3 },
  actions: { display: 'flex', gap: 6, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' },
  primary: { fontSize: 12, fontWeight: 600, padding: '6px 12px', borderRadius: 7,
    background: T.accent, color: '#fff', border: 'none', cursor: 'pointer' },
  ghost: { fontSize: 12, fontWeight: 600, padding: '6px 12px', borderRadius: 7,
    background: 'var(--btn-face,#313d4b)', color: 'var(--btn-ink,#e8eef7)', border: '1px solid var(--btn-edge,#56657a)', cursor: 'pointer' },
  state: { fontSize: 11, color: T.dim },
  sectionHead: { fontSize: 11.5, fontWeight: 600, color: T.text, marginTop: 6 },
  hint: { fontSize: 10.5, color: T.dim, lineHeight: 1.5 },
  notice: { fontSize: 11.5, color: T.text, background: 'color-mix(in srgb, var(--accent) 14%, transparent)',
    border: `1px solid ${T.accent}`, borderRadius: 7, padding: '7px 9px', cursor: 'pointer' },
  empty: { display: 'flex', flexDirection: 'column', gap: 6, padding: 14,
    border: `1px solid ${T.border}`, borderRadius: 9 },
  emptyHead: { fontSize: 13, fontWeight: 600, color: T.text },
};
